import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Modal from '../Modal';
import { Button, Input, LabelPicker, Toggle } from '../../shared/ui';
import StudentFamilyPicker from './StudentFamilyPicker';
import { API_URL } from '../../config/env';
import { setStudentLabels, createLabel as apiCreateLabel } from '../../services/labels';
import styles from '../../pages/DirectoryPage.module.css';

function authHeaders(extra = {}) {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, ...extra };
}

function emptyStudentForm() {
  return {
    student_id: '',
    first_name: '',
    last_name: '',
    dob: '',
    family_id: '',
    is_active: true
  };
}

function dobToInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const tz = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tz).toISOString().slice(0, 10);
}

/**
 * Create/edit student — same fields as directory student modal.
 * onSaved(savedRow, { mode }) after successful save.
 */
export default function StudentEditorModal({
  open,
  onClose,
  mode,
  record,
  labels,
  familiesLookup,
  onLabelsRefresh,
  onSaved,
  onAddNewFamily,
  flash,
  zIndex = 2000,
  familyModalOpen = false,
  selectFamilyId = null,
  closeOnBackdrop = true,
  closeOnEscape = true
}) {
  const formId = useId();
  const lastPickedFamily = useRef(null);
  const [studentForm, setStudentForm] = useState(emptyStudentForm());
  const [studentLabelIds, setStudentLabelIds] = useState([]);
  const [studentError, setStudentError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && record) {
      setStudentForm({
        ...emptyStudentForm(),
        ...record,
        dob: dobToInput(record.dob),
        family_id: record.family_id != null ? String(record.family_id) : '',
        is_active: typeof record.is_active === 'boolean' ? record.is_active : true
      });
      setStudentLabelIds((record.labels || []).map((l) => l.id));
    } else {
      setStudentForm(emptyStudentForm());
      setStudentLabelIds([]);
    }
    setStudentError('');
  }, [open, mode, record]);

  useEffect(() => {
    if (!open) {
      lastPickedFamily.current = null;
      return;
    }
    if (selectFamilyId == null) return;
    if (lastPickedFamily.current === selectFamilyId) return;
    lastPickedFamily.current = selectFamilyId;
    setStudentForm((f) => ({ ...f, family_id: String(selectFamilyId) }));
  }, [open, selectFamilyId]);

  const onStudentChange = (e) => {
    const { name, value, type, checked } = e.target;
    setStudentForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onCreateLabelInline = useCallback(
    async (name) => {
      try {
        const created = await apiCreateLabel({ name, color: 'neutral', kind: 'both' });
        onLabelsRefresh?.();
        return created;
      } catch (e) {
        flash(e.message, 'error');
        return null;
      }
    },
    [flash, onLabelsRefresh]
  );

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStudentError('');
    try {
      if (!studentForm.family_id) throw new Error('Please select a family');
      const isEdit = mode === 'edit';
      const url = isEdit ? `${API_URL}/api/students/${record.id}` : `${API_URL}/api/students`;
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          ...studentForm,
          family_id: Number(studentForm.family_id)
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to save student');
      }
      const saved = await res.json();
      await setStudentLabels(saved.id, studentLabelIds);
      flash(isEdit ? 'Student updated' : 'Student added');
      onSaved?.(saved, { mode: isEdit ? 'edit' : 'create' });
      onClose();
      onLabelsRefresh?.();
    } catch (err) {
      setStudentError(err.message);
      flash(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      zIndex={zIndex}
      closeOnEscape={closeOnEscape && !familyModalOpen}
      closeOnBackdrop={closeOnBackdrop && !familyModalOpen}
      title={mode === 'edit' ? 'Edit student' : 'Add student'}
      description={
        mode === 'edit'
          ? 'Update the student record below.'
          : 'Enroll a student under a family.'
      }
      size="lg"
    >
      <form onSubmit={submit} className={`${styles.form} ${styles.modalEntityForm}`}>
        <section className={styles.modalSection} aria-labelledby={`${formId}-enroll`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-enroll`} className={styles.modalSectionTitle}>
              Student & family
            </h4>
            <p className={styles.modalSectionHint}>
              Basic details and the household this student belongs to.
            </p>
          </header>
          <div className={styles.formGrid}>
            <Input
              label="CIN"
              name="student_id"
              value={studentForm.student_id}
              onChange={onStudentChange}
            />
            <Input
              label="First name"
              name="first_name"
              required
              value={studentForm.first_name}
              onChange={onStudentChange}
            />
            <Input
              label="Last name"
              name="last_name"
              value={studentForm.last_name}
              onChange={onStudentChange}
            />
            <Input
              label="Date of birth"
              name="dob"
              type="date"
              value={studentForm.dob || ''}
              onChange={onStudentChange}
            />
            <div className={styles.modalFullRow}>
              <StudentFamilyPicker
                id={`${formId}-family`}
                families={familiesLookup || []}
                value={studentForm.family_id}
                onChange={(id) => setStudentForm((f) => ({ ...f, family_id: id }))}
                onAddNew={onAddNewFamily}
              />
            </div>
          </div>
        </section>

        <section className={styles.modalSection} aria-labelledby={`${formId}-tags`}>
          <header className={styles.modalSectionHead}>
            <h4 id={`${formId}-tags`} className={styles.modalSectionTitle}>
              Labels & status
            </h4>
            <p className={styles.modalSectionHint}>Tags help you filter the directory.</p>
          </header>
          <div className={styles.modalTagBlock}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Labels</span>
              <LabelPicker
                compact
                available={(labels || []).filter((l) => l.kind === 'student' || l.kind === 'both')}
                selectedIds={studentLabelIds}
                onChange={setStudentLabelIds}
                onCreateLabel={onCreateLabelInline}
              />
            </div>
            <label className={styles.activeToggle}>
              <Toggle
                size="sm"
                checked={!!studentForm.is_active}
                onChange={(v) => setStudentForm((f) => ({ ...f, is_active: v }))}
                ariaLabel="Toggle active status"
              />
              <span>{studentForm.is_active ? 'Active' : 'Inactive'}</span>
            </label>
          </div>
        </section>

        {studentError ? <div className={styles.errorMsg}>{studentError}</div> : null}
        <div className={styles.formActions}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {mode === 'edit' ? 'Save changes' : 'Add student'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
